import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Plus, AlignStartVertical, HandCoins, Archive, Layers } from 'lucide-react';
import { useMediaQuery, Typography, Box, Skeleton, useTheme } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { formatPositionsDisplay } from '../../services/positionAPIs';
import PageHeader from '../../components/headers/Header';
import { getAllEmployees, getEmployeeImage } from '../../services/HRstaff';
import { getAllContracts, createContract, updateContract, deleteContract } from '../../services/ContractAPI';
import { API_URL } from '../../config';

const ContractOverview = lazy(() => import('../../components/ContractOverview'));
const CreateContract = lazy(() => import('../../components/CreateContract'));
const ContractTemplateManager = lazy(() => import('../../components/contractTemplates/ContractTemplateManager'));
const AllSalariesDashboard = lazy(() =>
  import('../../components/tables/SalaryTable').then(module => ({
    default: module.AllSalariesDashboard
  }))
);

const TabContentSkeleton = ({ height = "400px" }) => (
  <Box sx={{ width: '100%', height, p: 2 }}>
    <Skeleton variant="rectangular" width="100%" height="60px" sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" width="100%" height="200px" sx={{ mb: 2 }} />
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="rectangular" width="30%" height="100px" />
      <Skeleton variant="rectangular" width="30%" height="100px" />
      <Skeleton variant="rectangular" width="30%" height="100px" />
    </Box>
  </Box>
);

const useContractData = () => {
  const [contracts, setContracts] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [profileImages, setProfileImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    if (dataLoaded) return;

    try {
      setLoading(true);
      setError(null);

      const [contractsData, employeesData] = await Promise.all([
        getAllContracts(),
        getAllEmployees()
      ]);

      const employeesMap = new Map();
      employeesData.forEach(employee => {
        employeesMap.set(employee.employeeId, employee);
      });

      const transformedContracts = contractsData.map(contract => {
        const employee = employeesMap.get(contract.employeeId) || {};

        const statusUpper = (contract.status || 'UNKNOWN').toUpperCase();

        return {
          id: contract.contractId,
          contractId: contract.contractId,
          name: employee.firstName || 'N/A',
          surname: employee.lastName || 'N/A',
          userName: employee.user?.email?.split('@')[0] || employee.user?.userName || 'N/A',
          cin: employee.cin || 'N/A',
          type: contract.type,
          contractType: contract.type,
          job: formatPositionsDisplay(employee),
          position: formatPositionsDisplay(employee),
          office: employee.officeName || employee.department?.location || 'N/A',
          startContract: contract.startDate,
          startDate: contract.startDate,
          endOfContract: contract.endDate,
          endDate: contract.endDate,
          status: statusUpper,
          salary: contract.salary,
          workHours: contract.workHours,
          file: contract.file,
          employee: employee,
          userId: employee.employeeId,
          email: employee.email || 'N/A',
          dateOfBirth: employee.dob,
          phone: employee.phone || 'N/A',
          department: employee.department?.name ||  employee.departmentName || 'N/A',
          gender: employee.gender || 'N/A',
          cnss: employee.cnss || 'N/A',
          diploma: employee.diploma || 'N/A',
          dateOfJoining: employee.dateOfJoining || employee.joiningDate || employee.startDate,
          createdBy: contract.createdBy || 'SYSTEM',
          createdDate: contract.createdDate,
          lastModifiedBy: contract.lastModifiedBy || contract.createdBy || 'SYSTEM',
          lastModifiedDate: contract.lastModifiedDate || contract.createdDate,
          description: `${contract.type} contract for ${employee.position || 'N/A'}`,
          hasImage: !!employee.image,
        };
      });

      setContracts(transformedContracts);
      setEmployees(employeesData);

      // employees WITH active contracts (status not TERMINATED)
      const employeesWithActiveContracts = new Set(
        transformedContracts
          .filter(contract => contract.status !== 'TERMINATED')
          .map(contract => contract.userId)
      );

      // employees WITHOUT active contracts
      const employeesWithoutActiveContracts = employeesData.filter(employee =>
        !employeesWithActiveContracts.has(employee.employeeId)
      );

      const transformedUsers = employeesWithoutActiveContracts.map(emp => ({
        id: emp.employeeId,
        employeeId: emp.employeeId,
        name: emp.firstName || 'N/A',
        surname: emp.lastName || 'N/A',
        type: 'employee',
        email: emp.email || 'N/A',
        hasContract: false,
        position: formatPositionsDisplay(emp),
        office: emp.officeName || emp.department?.location || 'N/A',
        department: emp.departmentName || 'N/A',
        phone: emp.phone || 'N/A',
        cin: emp.cin || 'N/A',
        dateOfJoining: emp.dateOfJoining || emp.joiningDate || emp.startDate,
        dob: emp.dob,
        gender: emp.gender || 'N/A',
        role: emp.position || 'N/A',
        hasImage: !!emp.image,
        source: 'no_active_contract',
      }));

      setUsers(transformedUsers);
      setDataLoaded(true);

      // load images lazily
      requestIdleCallback(() => {
        loadProfileImages(employeesData);
      });
    } catch (error) {
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [dataLoaded]);

  const loadProfileImages = async (employees) => {
    const employeesWithImages = employees.filter(emp => emp.image);
    const batchSize = 5;

    for (let i = 0; i < employeesWithImages.length; i += batchSize) {
      const batch = employeesWithImages.slice(i, i + batchSize);
      const imagePromises = batch.map(async (emp) => {
        try {
          const imageUrl = await getEmployeeImage(emp.employeeId);
          return { id: emp.employeeId, imageUrl };
        } catch (err) {
          console.error(`Failed to load image for employee ${emp.employeeId}:`, err);
          return { id: emp.employeeId, imageUrl: null };
        }
      });

      try {
        const images = await Promise.all(imagePromises);
        setProfileImages(prev => {
          const newImages = images.reduce((acc, { id, imageUrl }) => {
            if (imageUrl) acc[id] = imageUrl;
            return acc;
          }, {});
          return { ...prev, ...newImages };
        });
      } catch (error) {
        console.error('Error loading image batch:', error);
      }

      if (i + batchSize < employeesWithImages.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };

  return {
    contracts,
    setContracts,
    users,
    setUsers,
    employees,
    profileImages,
    setProfileImages,
    loading,
    setLoading,
    error,
    setError,
    fetchData,
    dataLoaded
  };
};

const IsDirector =
  localStorage.getItem('userRole') === 'DIRECTOR' ||
  localStorage.getItem('userRole') === 'HR' ||
  localStorage.getItem('userRole') === 'DEV';

const ContractManagement = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [showSuccess, setShowSuccess] = useState(false);
  const isLargeScreen = useMediaQuery('(min-width: 1600px)');
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewContract, setViewContract] = useState(null);
  const [editContract, setEditContract] = useState(null);

  const {
    contracts,
    setContracts,
    users,
    setUsers,
    employees,
    profileImages,
    setProfileImages,
    loading,
    setLoading,
    error,
    setError,
    fetchData,
    dataLoaded
  } = useContractData();

  useEffect(() => {
    fetchData();

    return () => {
      Object.values(profileImages).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatAuditDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleContractSubmit = useCallback(async (formData, selectedUser, editingContract) => {
    try {
      setLoading(true);
      setError(null);

      if (editingContract) {
        const updatedContract = await updateContract(
          editingContract.contractId || editingContract.id,
          formData,
          formData.contractFile
        );

        const employee = employees.find(emp =>
          emp.employeeId === updatedContract.employeeId
        ) || editingContract.employee || {};

        const statusUpper = (updatedContract.status || editingContract.status || 'UNKNOWN').toUpperCase();

        const transformedUpdatedContract = {
          ...updatedContract,
          id: updatedContract.contractId,
          contractId: updatedContract.contractId,
          name: employee.firstName || editingContract.name,
          surname: employee.lastName || editingContract.surname,
          userName: employee.user?.email?.split('@')[0] || editingContract.userName,
          cin: employee.cin || editingContract.cin,
          type: updatedContract.type,
          contractType: updatedContract.type,
          job: employee.position || editingContract.job,
          position: employee.position || editingContract.position,
          office: employee.office?.name || employee.department?.location || editingContract.office,
          startContract: updatedContract.startDate,
          startDate: updatedContract.startDate,
          endOfContract: updatedContract.endDate,
          endDate: updatedContract.endDate,
          status: statusUpper,
          salary: updatedContract.salary,
          workHours: updatedContract.workHours,
          file: updatedContract.file,
          employee: employee,
          userId: employee.employeeId || editingContract.userId,
          email: employee.user?.email || editingContract.email,
          dateOfBirth: employee.dob || editingContract.dateOfBirth,
          phone: employee.phone || editingContract.phone,
          department: employee.department?.name || editingContract.department,
          gender: employee.gender || editingContract.gender,
          cnss: employee.cnss || editingContract.cnss,
          diploma: employee.diploma || editingContract.diploma,
          dateOfJoining: employee.dateOfJoining || editingContract.dateOfJoining,
          createdBy: updatedContract.createdBy || editingContract.createdBy || 'SYSTEM',
          createdDate: updatedContract.createdDate || editingContract.createdDate,
          lastModifiedBy: updatedContract.lastModifiedBy || updatedContract.createdBy || editingContract.lastModifiedBy || 'SYSTEM',
          lastModifiedDate: updatedContract.lastModifiedDate || updatedContract.createdDate || editingContract.lastModifiedDate,
          description: `${updatedContract.type} contract for ${employee.position || editingContract.position || 'N/A'}`,
          hasImage: !!employee.image || editingContract.hasImage,
        };

        setContracts(prev =>
          prev.map(c =>
            c.contractId === updatedContract.contractId
              ? transformedUpdatedContract
              : c
          )
        );
        setEditContract(null);

        if (employee.image) {
          const imageUrl = await getEmployeeImage(employee.employeeId);
          setProfileImages(prev => ({
            ...prev,
            [employee.employeeId]: imageUrl,
          }));
        }
      } else {
        const newContract = await createContract(
          selectedUser.employeeId || selectedUser.id,
          formData,
          formData.contractFile
        );

        const employee = employees.find(emp =>
          emp.employeeId === (selectedUser.employeeId || selectedUser.id)
        ) || selectedUser;

        const statusUpper = (newContract.status || 'ACTIVE').toUpperCase();

        const newTransformedContract = {
          id: newContract.contractId,
          contractId: newContract.contractId,
          name: employee.firstName || selectedUser.name || 'N/A',
          surname: employee.lastName || selectedUser.surname || 'N/A',
          userName: employee.user?.email?.split('@')[0] || selectedUser.email?.split('@')[0] || 'N/A',
          cin: employee.cin || selectedUser.cin || 'N/A',
          type: newContract.type,
          contractType: newContract.type,
          job: employee.position || selectedUser.position || 'N/A',
          position: employee.position || selectedUser.position || 'N/A',
          office: employee.office?.name || employee.department?.location || selectedUser.office || 'N/A',
          startContract: newContract.startDate,
          startDate: newContract.startDate,
          endOfContract: newContract.endDate,
          endDate: newContract.endDate,
          status: statusUpper,
          salary: newContract.salary,
          workHours: newContract.workHours,
          file: newContract.file,
          employee: employee,
          userId: employee.employeeId || selectedUser.id,
          email: employee.user?.email || selectedUser.email || 'N/A',
          dateOfBirth: employee.dob || selectedUser.dob,
          phone: employee.phone || selectedUser.phone || 'N/A',
          department: employee.department?.name || selectedUser.department || 'N/A',
          gender: employee.gender || selectedUser.gender || 'N/A',
          cnss: employee.cnss || selectedUser.cnss || 'N/A',
          diploma: employee.diploma || selectedUser.diploma || 'N/A',
          dateOfJoining: employee.dateOfJoining || selectedUser.dateOfJoining,
          createdBy: newContract.createdBy || 'SYSTEM',
          createdDate: newContract.createdDate,
          lastModifiedBy: newContract.lastModifiedBy || newContract.createdBy || 'SYSTEM',
          lastModifiedDate: newContract.lastModifiedDate || newContract.createdDate,
          description: `${newContract.type} contract for ${employee.position || selectedUser.position || 'N/A'}`,
          hasImage: !!employee.image || !!selectedUser.hasImage,
        };

        setContracts(prev => [...prev, newTransformedContract]);
        setUsers(prev => prev.filter(u => u.id !== (selectedUser.employeeId || selectedUser.id)));

        if (employee.image || selectedUser.hasImage) {
          const imageUrl = await getEmployeeImage(employee.employeeId || selectedUser.id);
          setProfileImages(prev => ({
            ...prev,
            [employee.employeeId || selectedUser.id]: imageUrl,
          }));
        }
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting contract:', error);
      setError(`Failed to save contract: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [setContracts, setUsers, employees, setProfileImages, setLoading, setError, setEditContract]);

  const handleDeleteContract = useCallback(async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    try {
      setLoading(true);
      setError(null);
      await deleteContract(contractId);
      setContracts(prev => prev.filter(contract => contract.contractId !== contractId));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting contract:', error);
      setError(`Failed to delete contract: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [setContracts, setLoading, setError]);

  const handleEditContract = useCallback((contract) => {
    setEditContract(contract);
    setActiveTab('add');
  }, []);

  const handleDownloadContract = useCallback(async (contract) => {
    try {
      setLoading(true);
      setError(null);
      const mergedPdf = await PDFDocument.create();
      const font = await mergedPdf.embedFont(StandardFonts.Helvetica);
      const boldFont = await mergedPdf.embedFont(StandardFonts.HelveticaBold);
      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margin = 50;
      const lineHeight = 20;
      const maxWidth = pageWidth - 2 * margin;

      let infoPage = mergedPdf.addPage([pageWidth, pageHeight]);
      let y = pageHeight - margin;

      const addSectionHeader = (title) => {
        if (y < margin + lineHeight) {
          infoPage = mergedPdf.addPage([pageWidth, pageHeight]);
          y = pageHeight - margin;
        }
        infoPage.drawText(title, { x: margin, y, size: 14, font: boldFont, color: rgb(0, 0, 0) });
        y -= lineHeight;
      };

      const wrapText = (text, maxW, fontUsed, fontSize) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const width = fontUsed.widthOfTextAtSize(testLine, fontSize);
          if (width <= maxW) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      const addField = (label, value) => {
        if (value && value !== 'N/A') {
          const text = `${label}: ${value}`;
          const lines = wrapText(text, maxWidth, font, 12);
          for (const line of lines) {
            if (y < margin + lineHeight) {
              infoPage = mergedPdf.addPage([pageWidth, pageHeight]);
              y = pageHeight - margin;
            }
            infoPage.drawText(line, { x: margin, y, size: 12, font, color: rgb(0, 0, 0) });
            y -= lineHeight;
          }
        }
      };

      infoPage.drawText('Employment Contract', {
        x: margin,
        y,
        size: 16,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      y -= lineHeight * 2;

      addSectionHeader('Personal Information');
      addField('Name', `${contract.name || 'N/A'} ${contract.surname || 'N/A'}`);
      addField('CIN', contract.cin || 'N/A');
      addField('Date of Birth', contract.dateOfBirth ? new Date(contract.dateOfBirth).toLocaleDateString() : 'N/A');
      addField('Email', contract.email || 'N/A');
      addField('Phone', contract.phone || 'N/A');
      y -= lineHeight;

      addSectionHeader('Contract Details');
      addField('Contract Type', contract.contractType || contract.type || 'N/A');
      addField('Start Date', contract.startContract ? new Date(contract.startContract).toLocaleDateString() : 'N/A');
      addField('End Date', contract.endOfContract ? new Date(contract.endOfContract).toLocaleDateString() : 'N/A');
      addField('Status', contract.status || 'N/A');
      addField('Salary', contract.salary ? `MAD ${contract.salary}` : 'N/A');
      addField('Work Hours', contract.workHours || 'N/A');
      y -= lineHeight;

      addSectionHeader('Audit Information');
      addField('Created By', contract.createdBy || 'SYSTEM');
      addField('Created Date', contract.createdDate ? formatAuditDate(contract.createdDate) : 'N/A');
      addField('Last Modified By', contract.lastModifiedBy || 'SYSTEM');
      addField('Last Modified Date', contract.lastModifiedDate ? formatAuditDate(contract.lastModifiedDate) : 'N/A');

      if (contract.file) {
        try {
          const fileUrl = `${API_URL}/contracts/contracts/${encodeURIComponent(contract.file)}`;
          const response = await fetch(fileUrl, {
            method: 'GET',
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (!response.ok) throw new Error(`Failed to fetch contract file: ${response.statusText}`);
          const contractPdfBytes = await response.arrayBuffer();
          const contractPdf = await PDFDocument.load(contractPdfBytes);
          const copiedPages = await mergedPdf.copyPages(contractPdf, contractPdf.getPageIndices());
          copiedPages.forEach(page => mergedPdf.addPage(page));
        } catch (error) {
          console.error('Error loading contract file:', error);
          const errorPage = mergedPdf.addPage([pageWidth, pageHeight]);
          errorPage.drawText(`Failed to load contract file: ${error.message}`, {
            x: margin,
            y: pageHeight - margin,
            size: 12,
            font,
            color: rgb(1, 0, 0),
          });
        }
      }

      const pdfBytes = await mergedPdf.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract_${contract.name}_${contract.surname}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error downloading contract:', error);
      setError(`Failed to download contract: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);

  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  }, [sortBy]);

  const handleViewContract = useCallback((contract) => setViewContract(contract), []);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (!dataLoaded && ['contracts', 'add', 'salaries', 'sHistory'].includes(tabId)) {
      fetchData();
    }
  }, [dataLoaded, fetchData]);

  const commonProps = {
    contracts,
    users,
    employees,
    profileImages,
    onDelete: handleDeleteContract,
    onDownload: handleDownloadContract,
    onEdit: handleEditContract,
    handleSort,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    viewContract,
    setViewContract,
    handleViewContract,
    showSuccess,
    setShowSuccess,
    error,
    onContractSubmit: handleContractSubmit,
    onEditContract: editContract,
  };

  const renderTabContent = () => {
    if (loading && !dataLoaded && activeTab !== 'overview' && activeTab !== 'archive') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading data...</Typography>
        </Box>
      );
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activeContracts = contracts.filter((c) => {
      if (c.status === 'EXPIRED' || c.status === 'TERMINATED') return false;
      
      const endDateStr = c.endOfContract || c.endDate;
      if (!endDateStr) return true; 
      
      const endDate = new Date(endDateStr);
      endDate.setHours(0, 0, 0, 0);
      return endDate >= today;
    });

    const archivedContracts = contracts.filter((c) => {
      if (c.status === 'EXPIRED' || c.status === 'TERMINATED') return true;
      
      const endDateStr = c.endOfContract || c.endDate;
      if (!endDateStr) return false; 
      
      const endDate = new Date(endDateStr);
      endDate.setHours(0, 0, 0, 0);
      return endDate < today;
    });

    return (
      <Suspense fallback={<TabContentSkeleton />}>
        {activeTab === 'overview' && (<ContractOverview {...commonProps} contracts={activeContracts} isArchiveView={false} />)}
        {activeTab === 'archive' && (<ContractOverview {...commonProps} contracts={archivedContracts} isArchiveView={true} />)}
        {activeTab === 'add' && (
          <CreateContract {...commonProps} />
        )}
        {activeTab === 'templates' && (<ContractTemplateManager />)}
        {IsDirector && activeTab === 'salaries' && <AllSalariesDashboard />}
      </Suspense>
    );
  };

  return (
    <div className={`Leaves-main-container ${theme?.palette?.mode === 'dark' ? 'dark' : ''}`}>
      {theme?.palette?.mode === 'dark' && (
        <style>{`
          .Leaves-main-container.dark .form-overlay {
            background: rgba(0, 0, 0, 0.35) !important;
          }
          
          /* Fix MUI dialog contrast and backgrounds in dark mode globally */
          .MuiDialog-root .MuiDialog-paper,
          .MuiDialog-root .MuiPaper-root,
          .MuiDialog-root .MuiCard-root {
            background-color: #1e293b !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
            border: 1px solid #334155 !important;
            color: #f1f5f9 !important;
          }
          
          .MuiDialog-root .MuiDialogTitle-root,
          .MuiDialog-root .MuiTypography-root,
          .MuiDialog-root .MuiFormLabel-root,
          .MuiDialog-root .MuiTableCell-root,
          .MuiDialog-root .MuiChip-label,
          .MuiDialog-root .MuiSvgIcon-root {
            color: #f1f5f9 !important;
          }
          
          .MuiDialog-root input,
          .MuiDialog-root select,
          .MuiDialog-root textarea,
          .MuiDialog-root .MuiInputBase-root {
            background-color: #334155 !important;
            color: #f1f5f9 !important;
          }
          
          .MuiDialog-root .MuiOutlinedInput-notchedOutline {
            border-color: #475569 !important;
          }
          
          .MuiDialog-root .MuiIconButton-root,
          .MuiDialog-root .lucide {
            color: #f1f5f9 !important;
          }

          /* Force the Dialog Container to pass color down if anything inherits */
          .MuiDialog-container {
            color: #f1f5f9 !important;
          }

          /* General card and paper fixes inside Leaves-main-container for Contracts section */
          .Leaves-main-container.dark .MuiPaper-root,
          .Leaves-main-container.dark .MuiCard-root {
             background-color: #1e293b !important;
             color: #f1f5f9 !important;
             border-color: #334155 !important;
          }
          
          .Leaves-main-container.dark .MuiTypography-root,
          .Leaves-main-container.dark .MuiTableCell-root {
             color: #f1f5f9 !important;
          }
        `}</style>
      )}
      <div className="Leaves-content-wrapper">
        <PageHeader
          title="Contract & Salaries Management"
          subtitle="Manage employment contracts and explore salaries updates"
        />

        {error && (
          <div className="error-message">
            <Typography color="error">{error}</Typography>
          </div>
        )}

        {showSuccess && (
          <div className="success-message">
            <Typography color="success">Operation completed successfully!</Typography>
          </div>
        )}

        <div className="Leaves-nav-tabs">
          <div className="Leaves-tab-bar">
            {[
              { id: 'overview', name: 'Overview', icon: AlignStartVertical },
              { id: 'archive', name: 'Contract Archive', icon: Archive },
              { id: 'add', name: 'Create Contract', icon: Plus },
              { id: 'templates', name: 'Templates & Clauses', icon: Layers },
              ...(IsDirector ? [{ id: 'salaries', name: 'Salaries', icon: HandCoins }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`Leaves-tab-btn ${activeTab === tab.id ? 'Leaves-tab-btn-active' : ''}`}
              >
                <tab.icon className="Leaves-tab-icon" size={18} />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
};

export default React.memo(ContractManagement);
